// Лента (README §3): события сервера по long-poll (docs/api-ui-v3.md §4).
//   1. GET /api/events[?source=]             → показать, запомнить cursor
//   2. цикл: GET /api/events?since=<cursor>&wait=25 → новые сверху
//   ошибка сети — пауза 5 с и снова.
// Фильтр источника — тот же запрос с source=; смена фильтра перезапускает цикл.

import { create } from 'zustand'
import { http } from '../custom/http'

export type SourceId = 'server' | 'telegram' | 'github' | 'limits' | 'notes'

export interface FeedEvent {
  id: string
  time: string
  ts: number
  day: string
  source: SourceId
  title: string
  detail: string
  ref?: { kind: string; path?: string }
}

export interface FeedSource {
  id: SourceId
  title: string
  available: boolean
  count: number
  lastAt: string | null
  reason?: string
}

interface Page { events: FeedEvent[]; cursor: number | null }

const WAIT = 25
const RETRY_MS = 5000
const KEEP = 400

interface FeedState {
  events: FeedEvent[]
  source: SourceId | null
  sources: FeedSource[]
  loaded: boolean
  error: string | null
  setSource: (id: SourceId | null) => void
  loadSources: () => Promise<void>
}

export const useFeed = create<FeedState>()((set, get) => ({
  events: [],
  source: null,
  sources: [],
  loaded: false,
  error: null,
  setSource: (id) => {
    if (get().source === id) return
    set({ source: id, events: [], loaded: false })
    if (running) restart()
  },
  loadSources: async () => {
    try {
      const { sources } = await http<{ sources: FeedSource[] }>('/events/sources')
      set({ sources })
    } catch {
      // оставим прежний список
    }
  },
}))

let running = 0
let generation = 0

const sleep = (ms: number) => new Promise((r) => window.setTimeout(r, ms))

async function loop(gen: number): Promise<void> {
  const q = () => {
    const src = useFeed.getState().source
    return src ? `&source=${src}` : ''
  }
  let cursor: number | null = null
  while (gen === generation) {
    try {
      if (cursor === null) {
        const page = await http<Page>(`/events?limit=200${q()}`)
        if (gen !== generation) return
        cursor = page.cursor ?? 0
        useFeed.setState({ events: page.events, loaded: true, error: null })
        continue
      }
      const page: Page = await http<Page>(`/events?since=${cursor}&wait=${WAIT}${q()}`, {}, (WAIT + 10) * 1000)
      if (gen !== generation) return
      if (page.events.length) {
        const seen = new Set(page.events.map((e) => e.id))
        useFeed.setState((s) => ({
          events: [...page.events, ...s.events.filter((e) => !seen.has(e.id))].slice(0, KEEP),
          error: null,
        }))
        void useFeed.getState().loadSources()
      }
      cursor = page.cursor ?? cursor
    } catch (error) {
      if (gen !== generation) return
      useFeed.setState({ error: (error as Error).message || 'нет связи', loaded: true })
      await sleep(RETRY_MS)
    }
  }
}

function restart(): void {
  generation += 1
  void loop(generation)
}

/** Запустить цикл, пока смонтирован вид Ленты. Возвращает остановку. */
export function startFeed(): () => void {
  running += 1
  if (running === 1) restart()
  void useFeed.getState().loadSources()
  return () => {
    running -= 1
    if (running === 0) generation += 1
  }
}
